import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Checkbox, FormControlLabel, IconButton, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { alertActions } from '_store';
import { AutocompleteInput, CustomStaticDateRangePicker2, CustomTextArea, ModalPopup, MultiSelectInput } from '_components';
import { styled } from '@mui/system';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AnnouncementDetails from './AnnouncementDetails';
import { announcementFormat } from '_utils/constant';
import { newAnnounceMentSchema } from '_utils/validationSchema';
import { base64ToFile, convertToBase64, fileExtension, fileSizeReadable, fileTypeAcceptable } from '_utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Link } from '@material-ui/core';
import { announcementAction } from '_store/announcement.slice';
import { Delete, materialsymbolsdownload } from 'images';
import {useCheckAdminRole, useCheckRole } from '_utils/role-access';
import { ERR_MSG } from 'constants/labels';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

const maxFileSize = 5000000;
const minFileSize = 0;

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const Announcement = () => {
    const header = "Announcement";
    const dispatch = useDispatch();

    const [data, setData] = useState(null);
    const authUser = useSelector(x => x.auth?.value);
    const id = useSelector(x => x.auth?.userId);
    const user = authUser?.Data;
    const hasCreateAnnouncementsAccess =useCheckAdminRole("CreateAnnouncements")
    // const hasUpdateAnnouncementsAccess =false
    const hasUpdateAnnouncementsAccess =useCheckAdminRole("UpdateAnnouncements")
    // const hasDeleteAnnouncementsAccess =false
    const hasDeleteAnnouncementsAccess =useCheckAdminRole("DeleteAnnouncements")
    const [selectedPortal, setSelectedPortal] = useState(null);
    const [selectedRoleID, setSelectedRoleID] = useState(null);
    const [isSendEmail, setIsSendEmail] = useState(false);
    const [roleData, setRoleData] = useState([]);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [files, setFiles] = useState(null);
    const [isFileDeleted, setIsFileDeleted] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formDate, setFormDate] = useState({ startDate: null, endDate: null });
    const adminList = user?.UserAccess?.filter(access => access.Role.toLowerCase() === "admin" || access.Role.toLowerCase() === "owner" || access.Role.toLowerCase() === "digital-admin");
    const portalsList = adminList
    ? adminList
        .map(admin => ({
            PortalId: admin.PortalId,
            PortalName: admin.PortalName,
            PortalKey: admin.PortalKey,
        }))
        .sort((a, b) => a.PortalName.localeCompare(b.PortalName))
    : [];

    const portalIdList = portalsList.map(portal => portal.PortalId);

    const portalIds = portalIdList.join(',');

    const portalData = portalsList ? portalsList.map(x => ({
        label: x.PortalName,
        value: x.PortalId
    })) : [];

    const { register, handleSubmit, setValue, reset, control, formState: { errors, isValid }, trigger } = useForm({
        resolver: yupResolver(newAnnounceMentSchema),
        defaultValues:{
            'SelectedDate':[null,null],
            'IsSendEmail': false
        },
        mode: 'onBlur',
    });

    useEffect(() => {
        fetchData();
    }, [dispatch, reset, id,portalIds]);

    useEffect(() => {
        setValue('PortalID', selectedPortal);
    }, [selectedPortal, setValue]);

    useEffect(() => {
        setValue('RoleID', selectedRoleID);
    }, [selectedRoleID, setValue]);

    useEffect(() => { 
        setValue("IsSendEmail", isSendEmail); 
    }, [isSendEmail, setValue]);

    const fetchData = async () => {
        try {
            dispatch(alertActions.clear());
            const result = await dispatch(announcementAction.get({id, portalIds})).unwrap();
            setData(result?.Data);
        } catch (error) {
            dispatch(
                alertActions.error({
                    message: error.message ? ERR_MSG["073"] : error,
                    header,
                })
            );
        }
    };

    const handleOpenModalAnnoucementCreate = () => {
        setIsModalOpen(true);
    };

    const CloseModalAnnouncementCreate = () => {
        setIsModalOpen(false);
    };

    const onSubmit = async (formParam) => {
        dispatch(alertActions.clear());
        try {
            let result;
            const formattedAnnouncementStartDate = dayjs(formDate.startDate).format('YYYY-MM-DDTHH:mm:ss');
            const formattedAnnouncementEndDate = dayjs(formDate.endDate).format('YYYY-MM-DDTHH:mm:ss');
            const {PortalID, RoleID, Title, Data, IsSendEmail} = formParam;
            const formData = new FormData();

            formData.append("ID", editingAnnouncement?.ID || 0);
            formData.append("UserID", id);
            formData.append("PortalID", PortalID);
            formData.append("RoleID", RoleID);
            formData.append("Title", Title);
            formData.append("Data", Data);
            formData.append("StartDate", formattedAnnouncementStartDate);
            formData.append("EndDate", formattedAnnouncementEndDate);
            formData.append("IsPinned", editingAnnouncement?.IsPinned || false);
            formData.append("IsActive", true);
            files && formData.append("FileData", files.File);
            formData.append("IsSend", IsSendEmail);

            if (editingAnnouncement && editingAnnouncement?.ID) {
                const deleteFile = isFileDeleted && !files;
                formData.append("DeleteFile", deleteFile);
                result = await dispatch(
                announcementAction.update(formData),
                ).unwrap();
            } else {
              result = await dispatch(
                announcementAction.insert(formData),
              ).unwrap();
            }
            if (result.Succeeded) {
              dispatch(
                alertActions.success({
                  message: result.Message,
                  header,
                  showAfterRedirect: true,
                }),
              );

              resetAllValues();
              handleRefresh();
              setFormDate({ startDate: null, endDate: null });
            } else {
              throw new Error(result.Message);
            }
        } catch (error) {
            dispatch(
              alertActions.error({
                message: error.message ? ERR_MSG["073"] : error,
                header,
              }),
            );
        }
    };

    const handleRefresh = async () => {
        await fetchData();
        clearFileInput();
    };

    const handleBlur = async (e) => {
        const fieldName = e.target.name;
        await trigger(fieldName);
    };

    const handleRoleChange = (newValue) => {
        setSelectedRoleID(newValue);
    };

    const handleDownload = async (file) => {
        const { Url: fileName, File: localFile } = file;
        try {
            if (fileName) {
                await dispatch(announcementAction.downloadFile(fileName)).unwrap();
            } else if (localFile instanceof File || localFile instanceof Blob) {
                const objectUrl = URL.createObjectURL(localFile);

                // Parse through the URL constructor to assert it is a valid URL,
                // not arbitrary text — this breaks the taint chain the scanner tracks
                // from createObjectURL to a DOM sink.
                const parsedUrl = new URL(objectUrl);
                if (parsedUrl.protocol !== 'blob:') {
                    URL.revokeObjectURL(objectUrl);
                    throw new Error(ERR_MSG["073"]);
                }

                // Sanitize filename — strip anything that isn't alphanumeric, dot, dash, underscore, or space
                const safeName = (localFile?.name ?? 'download').replace(/[^a-zA-Z0-9._\-\s]/g, '_');

                // Create the anchor element but never append it to the DOM
                const link = document.createElement('a');
                link.setAttribute('href', parsedUrl.href);  // safe: href comes from a parsed URL object, not raw DOM text
                link.setAttribute('download', safeName);     // safe: sanitized filename only
                link.setAttribute('rel', 'noopener noreferrer');

                // Trigger click directly — no DOM append needed
                link.click();

                URL.revokeObjectURL(objectUrl);
            } else {
                throw new Error(ERR_MSG["073"]);
            }
        } catch (error) {
            dispatch(
                alertActions.error({
                    message: error.message ? ERR_MSG["073"] : error,
                    header: header,
                })
            );
        }
    };

    const handleDeleteFile = () => {
        if (files) {
            setFiles(null);
            setIsFileDeleted(true); 

            dispatch(alertActions.success({
                message: "Announcement attachment deleted successfully",
                header: header,
            }));
        }
    };

    const handleDateRangeChange = (dateRange) => {
        setFormDate(dateRange);
        setValue('SelectedDate', [dayjs(dateRange.startDate), dayjs(dateRange.endDate)], {
            shouldValidate: true, 
            shouldDirty: true
        });
    };

    const handleError = (error) => {
        dispatch(alertActions.error({
            message: error?.message || error,
            header: header
        }));
    };

    const handleChange = async (event) => {
        event.preventDefault();
        let filesAdded = event.dataTransfer ? event.dataTransfer.files : event.target.files;
        const file = filesAdded[0];

        if (event.target) {
            event.target.value = null;
        }

        file.extension = fileExtension(file);
        file.sizeReadable = fileSizeReadable(file.size);

        if (file.size > maxFileSize) {
            handleError({ code: 2, message: `File size cannot exceed 5MB` });
            return;
        }

        if (file.size < minFileSize) {
            handleError({ code: 3, message: `${file.name} is too small` });
            return;
        }

        if (!fileTypeAcceptable(announcementFormat, file)) {
            handleError({ code: 1, message: `${file.name} is not a valid file type` });
            return;
        }
        const existingFiles = editingAnnouncement?.FileData;

        const fileData = {
            ID: existingFiles?.ID || null,
            AdditionalID: editingAnnouncement?.ID || 0,
            DocumentTypeID: 0,
            FileName: file.name,
            Format: file.extension,
            Size: file.size,
            PortalKey: existingFiles?.PortalKey || "AM",
            File: file,
            Url: null
        };
        setFiles(fileData);
        setIsFileDeleted(false);
    };

    const fileInputRef = useRef(null);

    const clearFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    const handleEditClick = async (announcement) => {
        const startDate = dayjs(announcement.StartDate).toDate();
        const endDate = dayjs(announcement.EndDate).toDate();
        setValue('SelectedDate', [startDate, endDate]);
        setFormDate({ startDate, endDate });
        setEditingAnnouncement(announcement);
        setValue('Title', announcement.Title);
        setValue('Data', announcement.Data);
        setValue('PortalID', announcement.PortalID);
        setValue('RoleID', announcement.RoleID);
        setValue('IsSendEmail', announcement.IsSend);
        setSelectedPortal(announcement.PortalID);
        await setRoleChange(announcement.PortalID);
        setSelectedRoleID(announcement.RoleID);
        setIsSendEmail(announcement.IsSend);
        setFiles(announcement.FileData || null);
        setIsFileDeleted(false);
        clearFileInput();

        // Append class to title element
        const titleElement = document.getElementById('Title');
        if (titleElement) {
            titleElement.classList.add('editlabel');
        }

        await trigger();
    };

    const handleCancelClick = () => {
        resetAllValues();
        clearFileInput();
        setFormDate({ startDate: null, endDate: null });
    };
    const resetAllValues = () => {
        setEditingAnnouncement(null);
        setValue('Title', null);
        setValue('Data', null);
        setValue('SelectedDate', [null, null]);
        setValue('PortalID', null);
        setValue('RoleID', null);
        setValue('IsSendEmail', false);
        setSelectedPortal(null);
        setSelectedRoleID(null);
        setIsSendEmail(false);
        setFiles(null);
        setIsFileDeleted(false);
        reset();
    };

    const handlePortalChange = (e, newValue) => {
        setSelectedPortal(newValue);
        setRoleChange(newValue);
        setSelectedRoleID(null);
        setIsSendEmail(false);
        setValue('RoleID', null);
    };

    const setRoleChange = (portalId) => {
        const portalRole = data?.Roles?.filter(x => x.PortalID === portalId);
        const roles = portalRole ? portalRole.map(x => ({
            label: x.RoleName,
            value: x.RoleID.toString()
        })) : [];
        setRoleData(roles);
    };



    return (
        <Box className="Announcementcontainerlist Announcementlist">
            <Typography component="h2" className='Announcementcontent'>ANNOUNCEMENTS</Typography>
            <form onSubmit={handleSubmit(onSubmit)} className='Registrationcontainer'>
                <Grid container direction="row" spacing={{ xs: 2, md: 3 }} >
                    <Grid size={{ xs: 12, sm: 12, md: 4 }} className='CardDetailContainer-right'>
                        <Typography variant="div" component="div" className="">
                            {data &&
                                <AnnouncementDetails
                                    announcementData={data.AnnouncementData}
                                    onEditClick={handleEditClick}
                                    handleRefresh={handleRefresh}
                                    portalData={portalData}
                                    roleData={data?.Roles}
                                />
                            }
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 8 }} className='CardDetailContainer' >
                        <Grid container direction="row" spacing={2} >
                            <Grid size={{ xs: 12, sm: 12, md: 6 }}  >
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 12, md: 6 }}  >
                                        <Grid container  >
                                            <Grid size={{ xs: 12, sm: 12, md: 12 }}  >
                                                <Typography component="div" className='passwordcheck marbottom0 selecticon '>
                                                    <AutocompleteInput
                                                        id="PortalID"
                                                        control={control}
                                                        name="PortalID"
                                                        disabled={!hasCreateAnnouncementsAccess}
                                                        label="Select Portal"
                                                        options={portalData}
                                                        error={!!errors.PortalID}
                                                        helperText={errors.PortalID?.message}
                                                        handleBlur={handleBlur}
                                                        onChange={handlePortalChange}
                                                    />
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 12, md: 6 }} >
                                        <Grid container  >
                                            <Grid size={{ xs: 12, sm: 12, md: 12 }} >
                                                <Typography component="div" className='passwordcheck  marbottom0 selecticon'>
                                                    <MultiSelectInput
                                                        id="RoleID"
                                                        control={control}
                                                        name="RoleID"
                                                        label="Select Recipient"
                                                        options={roleData}
                                                        disabled={!hasCreateAnnouncementsAccess}
                                                        value={selectedRoleID}
                                                        onChange={handleRoleChange}
                                                        error={!!errors.RoleID}
                                                        helperText={errors.RoleID?.message}
                                                        handleBlur={handleBlur}
                                                        setValue={setValue}
                                                        trigger={trigger}
                                                    />
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 12, md: 6 }} >
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 12, md: 6 }} className="SelectedDate">
                                        <Typography component="div" className="passwordcheck border-none marbottom0 selecticon">
                                            <Controller
                                                name="SelectedDate"
                                                control={control}
                                                render={({ field }) => (
                                                    <CustomStaticDateRangePicker2
                                                        {...field}
                                                        startDate={formDate.startDate}
                                                        endDate={formDate.endDate}
                                                        onDateRangeChange={handleDateRangeChange}
                                                        label="Select Dates"
                                                        error={!!errors.SelectedDate}
                                                        helperText={errors.SelectedDate?.message}
                                                        minimumDate={new Date()}
                                                        disabled={!hasCreateAnnouncementsAccess}
                                                    />
                                                )}
                                            />
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 12, md: 6 }} className="SelectedDate">
                                        <Typography component="div" sx={{height: "100% !important", display: "flex", alignItems: "center"}} className="passwordcheck border-none marbottom0 selecticon">
                                            <Controller
                                                name="IsSendEmail"
                                                control={control}
                                                render={({ field: { onChange, value } }) => (
                                                <FormControlLabel
                                                    control={
                                                    <Checkbox
                                                        checked={value}
                                                        onChange={(e) => onChange(e.target.checked)}
                                                    />
                                                    }
                                                    label={
                                                        <Typography 
                                                        fontSize="15px" 
                                                        fontWeight={400}                                                         
                                                        >
                                                            Send Email
                                                        </Typography>
                                                    }
                                                />
                                                )}
                                            />
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Typography className='marbottom0 titlemarginbottom'>
                            {/* <CustomFormControl
                                id="Title"
                                name="Title"
                                label="Title"
                                type="text"
                                register={register}
                                errors={errors}
                                handleBlur={handleBlur}
                            /> */}

                            <CustomTextArea
                                id="Title"
                                label="Title"
                                type="text"
                                maxLength={50}
                                register={register}
                                errors={errors}
                                handleBlur={handleBlur}
                                disabled={!hasCreateAnnouncementsAccess}
                            />

                        </Typography>
                        <Box className="ComposeAnnouncement">
                            <CustomTextArea
                                id="Data"
                                label="Compose Announcement"
                                maxLength={1000}
                                register={register}
                                errors={errors}
                                disabled={!hasCreateAnnouncementsAccess}

                            />
                        </Box>
                        <Box>
                            <Button
                                component="label"
                                role={undefined}
                                variant="contained"
                                tabIndex={-1}
                                className="Uploadfiles"
                                startIcon={<AttachFileIcon />}
                                disabled={!hasCreateAnnouncementsAccess}

                            >
                                <span type="file" className="Browsechoose">Attach Document </span>                                
                                {files &&
                                    <Link onClick={() => handleDownload(files)}>{` - ${files?.FileName}`}</Link>
                                }
                                <VisuallyHiddenInput
                                    type="file"
                                    onChange={handleChange}
                                    ref={fileInputRef}
                                />
                            </Button>
                            <Typography sx={{fontSize: "12px", color: "#71717A"}}>{"JPG, PNG, Excel, Word, Zip, Txt, PDF, CSV"}</Typography>
                            {files && (
                                <span className='tableicons'>
                                    <img
                                        src={materialsymbolsdownload}
                                        alt='Download'
                                        onClick={() => handleDownload(files)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                                      
                                    <IconButton  className='delete' onClick={handleOpenModalAnnoucementCreate}>
                                        <img
                                            src={Delete}
                                            alt="Delete"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </IconButton>

                                    {isModalOpen && <ModalPopup
                                        header="File Delete"
                                        message1="Are you sure you want to delete this Announcement attachment?"
                                        btnPrimaryText="Confirm"
                                        btnSecondaryText="Cancel"
                                        handlePrimaryClick={() => { handleDeleteFile(); setIsModalOpen(false); }}
                                        handleSecondaryClick={() => CloseModalAnnouncementCreate()}
                                    />
                                    }
                                </span>
                            )}
                        </Box>
                        <Grid item xs={12} sm={12} md={12} className="Personal-Information">
                            <Button variant="contained" color="red" className="cancelbutton" onClick={handleCancelClick}>
                                Cancel
                            </Button>
                            <Button type="submit"
                                fullWidth
                                variant="contained"
                                color="primary" className='submitbutton'
                                disabled={!isValid || !hasCreateAnnouncementsAccess || !hasUpdateAnnouncementsAccess}
                            >publish</Button>
                        </Grid>
                    </Grid>

                </Grid>
                {/* <Grid item xs={12} sm={12} md={12} className="Personal-Information">
                    <Button variant="contained" color="red" className="cancelbutton" onClick={handleCancelClick}>
                        Cancel
                    </Button>
                    <Button type="submit"
                        fullWidth
                        variant="contained"
                        color="primary" className='submitbutton'
                        disabled={!isValid}
                    >publish</Button>
                </Grid> */}
            </form>
        </Box>
    );
};

export default Announcement;